import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { get } from "@/lib/api";
import { formatDate, formatDateTime, formatNumber, formatUsd } from "@/lib/format";
import { ErrorState, KeyValue, TableSkeleton } from "@/components/common/DataStates";
import { Badge } from "@/components/ui/badge";
import { formatAdminRole, formatAdminRunStatus, formatAdminStatus } from "./adminFormat";

function ReadOnlyTable({ title, headers, rows, empty, minWidth = "600px" }) {
  return (
    <section className="og-card min-w-0 overflow-hidden">
      <h3 className="border-b border-border px-5 py-4 font-heading text-base font-semibold">{title}</h3>
      {!rows?.length ? <p className="p-5 text-sm text-muted-foreground">{empty}</p> : (
        <div className="max-w-full overflow-x-auto" role="region" aria-label={title} tabIndex={0}>
          <table className="w-full text-left text-sm" style={{ minWidth }}>
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>{headers.map((label) => <th key={label} scope="col" className="whitespace-nowrap px-4 py-3 font-medium">{label}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-border">{rows.map(({ key, cells }) => (
              <tr key={key} className="hover:bg-muted/30">{cells.map((cell, index) => (
                index === 0
                  ? <th key={index} scope="row" className="break-words px-4 py-3 font-medium">{cell}</th>
                  : <td key={index} className="break-words px-4 py-3">{cell}</td>
              ))}</tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function PlatformOrganizationDetailPage() {
  const { orgId } = useParams();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["platform-admin", "organization-detail", orgId],
    queryFn: () => get(`/admin/organizations/${encodeURIComponent(orgId)}`),
    retry: false,
  });
  const back = <Link to="/app/admin/organizations" className="og-focus inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"><ArrowLeft className="h-4 w-4" aria-hidden="true" />Retour aux organisations</Link>;

  return (
    <div className="min-w-0 space-y-6" data-testid="platform-organization-detail">
      {back}
      {isLoading ? <div className="og-card"><TableSkeleton testId="platform-organization-detail-loading" /></div>
        : isError ? error?.status === 404 || error?.code === "organization_not_found"
          ? <section className="og-card p-6"><h2 className="font-heading text-xl font-semibold">Organisation introuvable</h2><p className="mt-2 text-sm text-muted-foreground">Cette organisation n’existe pas ou n’est plus disponible.</p></section>
          : <ErrorState message="Impossible de charger les détails de l’organisation." onRetry={refetch} />
          : data ? (
            <>
              <header className="og-card min-w-0 p-5 sm:p-6">
                <p className="og-section-label">Organisation · lecture seule</p>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <h2 className="min-w-0 break-words font-heading text-2xl font-semibold">{data.name}</h2>
                  <Badge variant="outline">{formatAdminStatus(data.status)}</Badge>
                </div>
                <KeyValue className="mt-5" items={[
                  { label: "Plan", value: data.plan_code || "—" },
                  { label: "Abonnement", value: formatAdminStatus(data.subscription_status) },
                  { label: "Créée le", value: formatDate(data.created_at) },
                ]} />
              </header>
              <section className="og-card min-w-0 p-5 sm:p-6">
                <h3 className="font-heading text-base font-semibold">Informations générales</h3>
                <KeyValue className="mt-4" items={[
                  { label: "Langue", value: data.default_locale || "—" },
                  { label: "Devise", value: data.default_currency || "—" },
                  { label: "Pays", value: data.country || "—" },
                  { label: "Intégration initiale terminée", value: data.onboarding_completed ? "Oui" : "Non" },
                ]} />
              </section>
              <ReadOnlyTable title="Membres" headers={["Nom", "Email", "Rôle", "Statut"]} empty="Aucun membre." rows={data.members?.map((member) => ({
                key: member.id, cells: [member.full_name, member.email, formatAdminRole(member.role), formatAdminStatus(member.status)],
              }))} />
              <ReadOnlyTable title="Agents" headers={["Agent", "Identifiant", "Activé"]} empty="Aucun agent configuré." rows={data.agents?.map((agent) => ({
                key: agent.agent_id, cells: [agent.persona, agent.agent_id, agent.enabled ? "Oui" : "Non"],
              }))} minWidth="480px" />
              <ReadOnlyTable title="Exécutions récentes" headers={["Agent", "Tâche", "Statut", "Créée le", "Durée"]} empty="Aucune exécution récente." rows={data.recent_runs?.map((run) => ({
                key: run.id, cells: [run.agent_id, run.task_type, formatAdminRunStatus(run.status), formatDateTime(run.created_at), run.duration_ms == null ? "—" : `${formatNumber(run.duration_ms)} ms`],
              }))} minWidth="720px" />
              <ReadOnlyTable title="Usage IA par agent" headers={["Agent", "Appels", "Coût USD"]} empty="Aucun usage IA ce mois-ci." rows={data.ai_by_agent?.map((usage) => ({
                key: usage.agent_id || "sans-agent", cells: [usage.agent_id || "Sans agent", formatNumber(usage.calls), formatUsd(usage.cost_usd)],
              }))} minWidth="480px" />
            </>
          ) : null}
    </div>
  );
}